/**
 * Locked Features Configuration
 *
 * This file defines which features are locked for specific Clerk user roles.
 * When the logged-in user holds one of the roles in a feature's
 * `lockedForRoles` array, the feature is hidden or replaced with a lock screen.
 *
 * Usage:
 * 1. Add your feature key to the LOCKED_FEATURES object
 * 2. List the roles that should be denied access in `lockedForRoles`
 * 3. Add a description explaining what the feature does
 * 4. Optionally customize the lock screen messages
 * 5. Wrap your page component with <FeatureLock featureKey="your-feature-key">
 *    or call useIsFeatureLocked('your-feature-key') for inline checks.
 *
 * Example:
 *   LOCKED_FEATURES['my-feature'] = {
 *     lockedForRoles: ['org:distributor'],
 *     description: 'A brief explanation of what this feature does and its value.',
 *     title: 'Feature Locked',
 *     message: 'This feature is not available.',
 *     subMessage: 'Contact your administrator for access.'
 *   }
 */

import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';
import i18n from 'i18n';

// Display fields are getters (not plain strings) so every consumer of this
// module-scope config reads a live-translated value through i18n.t() without
// needing its own useTranslation() — this file isn't a component, and
// getLockedFeatureConfig() is called from several unrelated modules that
// only ever read `.title`/`.description`/`.message`/`.subMessage` as plain
// strings (see FeatureLock.jsx). Keeps every call site unchanged.
const sharedTitle = () => i18n.t('common:lockedFeature.title', 'Feature Locked');
const sharedSubMessage = () =>
  i18n.t(
    'common:lockedFeature.subMessage',
    'Please contact your administrator for more information about accessing this feature.',
  );

export const LOCKED_FEATURES = {
  'operator-premiums': {
    lockedForRoles: ['org:distributor'],
    get description() {
      return i18n.t(
        'common:lockedFeature.operatorPremiums.description',
        'The Premiums feature calculates the expected premiums your manufactured products will experience due to additional CBAM (Carbon Border Adjustment Mechanism) costs. This helps you understand and plan for the carbon-related cost impacts on your exports to the EU.',
      );
    },
    get title() {
      return sharedTitle();
    },
    get message() {
      return i18n.t(
        'common:lockedFeature.operatorPremiums.message',
        'The Premiums feature is not available for your account.',
      );
    },
    get subMessage() {
      return sharedSubMessage();
    },
  },

  'operator-exports': {
    lockedForRoles: ['org:distributor'],
    get description() {
      return i18n.t(
        'common:lockedFeature.operatorExports.description',
        'The Exports feature allows you to manage and track your product exports to the EU. You can create export records, view export history, and generate customer emissions reports for your buyers to support their CBAM compliance requirements.',
      );
    },
    get title() {
      return sharedTitle();
    },
    get message() {
      return i18n.t(
        'common:lockedFeature.operatorExports.message',
        'The Exports feature is not available for your account.',
      );
    },
    get subMessage() {
      return sharedSubMessage();
    },
  },

  'operator-diagrams': {
    lockedForRoles: ['org:distributor'],
    get description() {
      return i18n.t(
        'common:lockedFeature.operatorDiagrams.description',
        'The Diagrams feature allows you to create visual representations of your production processes, mapping inputs through production to finished goods.',
      );
    },
    get title() {
      return sharedTitle();
    },
    get message() {
      return i18n.t(
        'common:lockedFeature.operatorDiagrams.message',
        'The Diagrams feature is not available for your account.',
      );
    },
    get subMessage() {
      return sharedSubMessage();
    },
  },

  'operator-owned-installations': {
    lockedForRoles: ['org:distributor'],
    get description() {
      return i18n.t(
        'common:lockedFeature.operatorOwnedInstallations.description',
        'The Owned Installations tab shows installations that belong directly to your organization.',
      );
    },
    get title() {
      return sharedTitle();
    },
    get message() {
      return i18n.t(
        'common:lockedFeature.operatorOwnedInstallations.message',
        'The Owned Installations feature is not available for your account.',
      );
    },
    get subMessage() {
      return sharedSubMessage();
    },
  },

  'team-management': {
    lockedForRoles: ['org:distributor'],
    get description() {
      return i18n.t(
        'common:lockedFeature.teamManagement.description',
        'The Team Management page lets you invite members and manage their organization roles.',
      );
    },
    get title() {
      return sharedTitle();
    },
    get message() {
      return i18n.t(
        'common:lockedFeature.teamManagement.message',
        'The Team Management feature is not available for your account.',
      );
    },
    get subMessage() {
      return sharedSubMessage();
    },
  },
};

/**
 * Pure check: returns true if any of the feature's `lockedForRoles` matches
 * via the supplied `has` function.
 *
 * Post-Mongo-pivot the `has` argument is PermissionsProvider's role/permission
 * checker, not Clerk's. The shape (`({ role: string }) => boolean`) is the
 * same; bare and `org:`-prefixed slugs both resolve.
 *
 * @param {string} featureKey
 * @param {(opts: { role: string }) => boolean} has
 * @returns {boolean}
 */
export const isFeatureLocked = (featureKey, has) => {
  const config = LOCKED_FEATURES[featureKey];
  if (!config || typeof has !== 'function') return false;
  return config.lockedForRoles.some((role) => has({ role }));
};

/**
 * Get the lock screen configuration for a feature
 * @param {string} featureKey
 * @returns {object|null}
 */
export const getLockedFeatureConfig = (featureKey) => LOCKED_FEATURES[featureKey] || null;

/**
 * Hook: returns true if the current user's role locks the given feature.
 */
export const useIsFeatureLocked = (featureKey) => {
  const { has } = usePermissionsContext();
  return isFeatureLocked(featureKey, has);
};
